import { Fragment, useEffect, useRef, useState, type ReactElement, type Ref } from 'react';
import { Link, useLocation } from 'react-router';
import { PanelLeftClose, PanelLeftOpen, X } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { BRAND } from '@/config/brand';
import type { NavRow } from '@/config/navigation';
import { usePresence } from '@/hooks/use-presence';
import { isDesktopViewport } from '@/hooks/use-sidebar-state';
import { getActiveRowId } from '@/lib/sidebar-nav';
import { cn } from '@/lib/utils';

/** The navigation landmark's id: both toggles' `aria-controls` point at it. */
export const NAV_ID = 'dieu-huong-chinh';

/**
 * The toggle shortcut as this platform's keyboard spells it. The handler in app-shell.tsx accepts both Ctrl+\ and
 * ⌘\ everywhere; the tooltip and `aria-keyshortcuts` name only the one a person here would press.
 */
function sidebarShortcut(): { hint: string; aria: string } {
  const nav = typeof navigator === 'undefined' ? undefined : navigator;
  const uaData = (nav as { userAgentData?: { platform?: string } } | undefined)?.userAgentData;
  const platform = uaData?.platform || nav?.platform || '';
  return /mac|iphone|ipad|ipod/i.test(platform)
    ? { hint: '⌘\\', aria: 'Meta+Backslash' }
    : { hint: 'Ctrl \\', aria: 'Control+Backslash' };
}

interface SidebarProps {
  rows: readonly NavRow[];
  isOpen: boolean;
  /** The foot toggle: collapses or expands the sidebar on a desktop. */
  onToggle: () => void;
  /** Closes the drawer from inside it (scrim, close button) and hands focus back to the banner's toggle. */
  onClose: () => void;
  /** A link was followed: closes the drawer on a phone, leaves a desktop's choice alone. */
  onNavigate: () => void;
}

/*
 * Sizes (PO, 2026-09-25, option A). A tile is 40 px under a mouse on a desktop and 44 px wherever a thumb may be
 * the pointer: the phone drawer below `lg`, a touch screen at any width. Collapsed, the bar is 56 px: 8 + 40 + 8
 * under a mouse, 6 + 44 + 6 under a thumb. These are links, which the global touch floor in styles.css does not
 * reach, so these classes are their only floor.
 */
const TILE = 'size-11 lg:pointer-fine:size-10';
const ROW = 'h-11 lg:pointer-fine:h-10';
const GUTTER = 'px-2 lg:pointer-coarse:px-1.5';
/** On the dark bar the product's inset ring (primary on 15 % lightness) would not show: the ring is white there. */
const RING = 'focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-white/85';

/**
 * The one sidebar: the rail and the page panel merged (PO, 2026-09-25, option A). A dark `--h-rail-*` bar that
 * re-tints with the theme, one row per destination, and every destination one click away in either state.
 *
 *   - COLLAPSED (desktop default): 56 px, icons only. A row's label stays in the DOM as its accessible name
 *     (`lg:sr-only`) and shows in a tooltip on hover AND on keyboard focus. Hovering opens nothing else: there is
 *     no flyout, and the bar never expands on its own.
 *   - EXPANDED: 216 px, icons and labels, and the content column moves over (app-shell.tsx).
 *   - The foot's "Thu gọn menu" / "Mở rộng menu" toggles between them (Ctrl+\ / ⌘\ too, in app-shell.tsx). It
 *     sits on what it controls, with panel icons: ☰ means only "Việc lớn" on the Workplace.
 *   - PHONE (< lg): a 256 px drawer over everything, rows always labelled, opened from the banner's "Mở menu".
 *     A closed drawer is `visibility: hidden`, out of the tab order and the accessibility tree. Opening it moves
 *     the focus to its close button, "Đóng điều hướng" (never "Đóng menu", the banner toggle's name).
 *
 * A 1 px line parts the row groups (`NavRow.group`), expanded and collapsed alike. It is `aria-hidden`: the rows
 * are one list to a screen reader.
 */
export function Sidebar({ rows, isOpen, onToggle, onClose, onNavigate }: SidebarProps) {
  const { pathname } = useLocation();
  const activeId = getActiveRowId(rows, pathname);
  // Labels hide only on a collapsed desktop; the phone drawer is always labelled.
  const collapsed = !isOpen;

  // A drawer that opens on a phone takes the focus with it, so the next Tab walks the menu the person asked
  // for rather than the page under the scrim.
  const closeButton = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (isOpen && !isDesktopViewport()) closeButton.current?.focus();
  }, [isOpen]);

  // The scrim fades with the drawer (styles/motion.css `.h-scrim`); on a desktop it is `display: none` and goes at once.
  const scrim = usePresence<HTMLDivElement>(isOpen);

  const renderRow = (row: NavRow, index: number) => (
    <Fragment key={row.id}>
      {index > 0 && rows[index - 1]!.group !== row.group && (
        <li aria-hidden="true" data-nav-divider="" className="list-none">
          <span className="mx-1.5 my-1.5 block h-px bg-[hsl(var(--h-rail-divider))]" />
        </li>
      )}
      <li>
        <NavRowLink row={row} isActive={row.id === activeId} collapsed={collapsed} onNavigate={onNavigate} />
      </li>
    </Fragment>
  );

  return (
    <TooltipProvider delayDuration={0}>
      {/* The scrim only exists on a phone with the drawer open; a tap on it closes the drawer. */}
      {scrim.mounted && (
        <div
          ref={scrim.ref} data-state={scrim.state} aria-hidden="true"
          className="h-scrim fixed inset-0 z-40 bg-black/30 lg:hidden" onClick={onClose}
        />
      )}

      <nav
        id={NAV_ID}
        aria-label="Điều hướng chính"
        className={cn(
          'fixed left-0 top-0 z-50 flex h-screen flex-col overflow-hidden bg-[hsl(var(--h-rail-bg))]',
          'text-[hsl(var(--h-rail-icon))] shadow-[var(--h-shadow-lg)] lg:shadow-none',
          // The phone drawer slides in over 220 ms and out over 160 ms, on the motion tokens (app-tokens.css); the
          // desktop's width change keeps its 200 ms.
          'lg:duration-200 lg:ease-out motion-reduce:transition-none',
          // A closed phone drawer is `visibility: hidden` (out of the tab order and the accessibility tree).
          // Visibility is transitioned only on the way OUT, so the drawer stays visible while it slides away.
          // On the way IN it must not be: a transition from hidden starts at "hidden", and the focus the
          // drawer takes as it opens would land on a hidden button and be dropped — measured in WebKit
          // (focus left on <body>) and Chromium (left on the toggle) at 390/360 px.
          isOpen
            ? 'w-64 translate-x-0 transition-[translate,width] duration-[220ms] ease-(--h-ease-out) lg:w-[216px]'
            : cn('w-64 -translate-x-full max-lg:invisible lg:w-14 lg:translate-x-0 transition-[translate,width,visibility]',
              'duration-[160ms] ease-(--h-ease-in)'),
        )}
      >
        {/* Head: as tall as the banner, logo tile, the product's name, and the drawer's ✕ on a phone. */}
        <div className={cn('flex h-13 shrink-0 items-center gap-2.5', GUTTER)}>
          <Link
            to="/"
            aria-label={BRAND.name}
            onClick={onNavigate}
            className={cn('flex shrink-0 items-center justify-center rounded-[10px]', TILE, RING)}
          >
            <span className="flex size-full items-center justify-center rounded-[10px] bg-white shadow-[var(--h-shadow-sm)]">
              <img src={BRAND.logoSrc} alt="" width={26} height={26} className="object-contain" />
            </span>
          </Link>
          <span
            aria-hidden="true"
            className={cn('truncate text-[15px] font-extrabold text-white', collapsed && 'lg:hidden')}
          >
            {BRAND.name}
          </span>
          {isOpen && (
            <button
              type="button"
              ref={closeButton}
              onClick={onClose}
              aria-label="Đóng điều hướng"
              className={cn(
                'ml-auto flex size-11 shrink-0 items-center justify-center rounded-lg transition-colors hover:bg-[hsl(var(--h-rail-hover-bg))] hover:text-white lg:hidden',
                RING,
              )}
            >
              <X className="size-5" aria-hidden="true" />
            </button>
          )}
        </div>

        <ul className={cn('flex min-h-0 flex-1 flex-col gap-0.5 overflow-y-auto overflow-x-hidden py-1.5', GUTTER)}>
          {rows.map(renderRow)}
        </ul>

        {/* Foot: the desktop's collapse toggle. A phone opens and closes the drawer from the banner instead. */}
        <div className="hidden shrink-0 border-t border-[hsl(var(--h-rail-divider))] p-2 lg:block lg:pointer-coarse:px-1.5">
          <SidebarCollapseToggle isOpen={isOpen} onClick={onToggle} />
        </div>
      </nav>
    </TooltipProvider>
  );
}

/**
 * A tooltip to the right of a collapsed row, on hover and on keyboard focus (Radix opens on both). Controlled so
 * that expanding the bar turns it off without remounting the trigger, which would drop the focus.
 */
function SideTip({
  label, shortcut, enabled, children,
}: { label: string; shortcut?: string; enabled: boolean; children: ReactElement }) {
  const [open, setOpen] = useState(false);
  // Radix reports a change only against the `open` it was given. While the tip is disabled that is always
  // false, so the pointer entering an expanded row reports `true` and leaving it reports nothing: the row would
  // stay "open" and show its tip the moment the bar collapses. So nothing opens while disabled, and a tip that
  // was open when it got disabled is forgotten.
  useEffect(() => {
    if (!enabled) setOpen(false);
  }, [enabled]);
  return (
    <Tooltip open={enabled && open} onOpenChange={(next) => setOpen(enabled && next)}>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      {/* The mock's near-black tip (`--h-tooltip-bg` / `--h-tooltip-fg`, overridable per theme), 5 × 9 px, 12 px semibold. */}
      <TooltipContent
        side="right"
        sideOffset={8}
        className="pointer-events-none rounded-md bg-[hsl(var(--h-tooltip-bg))] px-2.25 py-1.25 text-[12px] font-semibold text-[hsl(var(--h-tooltip-fg))] shadow-[var(--h-shadow-md)]"
      >
        {label}
        {shortcut !== undefined && (
          <>
            {' '}
            <kbd className="ml-0.5 rounded bg-white/15 px-1 font-[inherit] text-[11px]">{shortcut}</kbd>
          </>
        )}
      </TooltipContent>
    </Tooltip>
  );
}

/** One destination: icon tile and label. Active = white 16 % fill, a 22 % white inset line, bold white text. */
function NavRowLink({
  row, isActive, collapsed, onNavigate,
}: { row: NavRow; isActive: boolean; collapsed: boolean; onNavigate: () => void }) {
  const Icon = row.icon;
  return (
    <SideTip label={row.label} enabled={collapsed}>
      <Link
        to={row.href}
        aria-current={isActive ? 'page' : undefined}
        onClick={onNavigate}
        className={cn(
          'flex w-full items-center gap-1 whitespace-nowrap rounded-[10px] text-[12px] transition-colors', ROW, RING,
          isActive
            ? 'bg-[hsl(var(--h-rail-active-bg))] font-bold text-[hsl(var(--h-rail-active-fg))] shadow-[inset_0_0_0_1px_hsl(0_0%_100%/0.22)]'
            : 'font-semibold hover:bg-[hsl(var(--h-rail-hover-bg))] hover:text-white',
        )}
      >
        <span aria-hidden="true" className={cn('flex shrink-0 items-center justify-center', TILE)}>
          <Icon className="size-5" />
        </span>
        <span className={cn('min-w-0 truncate pr-2', collapsed && 'lg:sr-only')}>{row.label}</span>
      </Link>
    </SideTip>
  );
}

/**
 * The desktop's "Thu gọn menu" / "Mở rộng menu", at the foot of the bar it controls. Panel icons, never ☰. Its
 * tooltip names the shortcut in both states; collapsed, it is also the only place its name shows.
 */
function SidebarCollapseToggle({ isOpen, onClick }: { isOpen: boolean; onClick: () => void }) {
  const label = isOpen ? 'Thu gọn menu' : 'Mở rộng menu';
  const Icon = isOpen ? PanelLeftClose : PanelLeftOpen;
  const shortcut = sidebarShortcut();
  return (
    <SideTip label={label} shortcut={shortcut.hint} enabled>
      <button
        type="button"
        onClick={onClick}
        aria-controls={NAV_ID}
        aria-expanded={isOpen}
        aria-keyshortcuts={shortcut.aria}
        className={cn(
          'flex w-full items-center gap-1 whitespace-nowrap rounded-[10px] text-left text-[12px] font-semibold transition-colors hover:bg-[hsl(var(--h-rail-hover-bg))] hover:text-white',
          ROW, RING,
        )}
      >
        <span aria-hidden="true" className={cn('flex shrink-0 items-center justify-center', TILE)}>
          <Icon className="size-5" />
        </span>
        <span className={cn('min-w-0 truncate pr-2', !isOpen && 'lg:sr-only')}>{label}</span>
      </button>
    </SideTip>
  );
}

/**
 * The banner's drawer toggle, on a phone only (< lg): a desktop collapses from the sidebar's foot. It names the
 * state it will change to, controls the navigation landmark, and carries a panel icon, not ☰.
 */
export function SidebarToggle({
  onClick, isOpen, ref,
}: { onClick: () => void; isOpen: boolean; ref?: Ref<HTMLButtonElement> }) {
  return (
    <button
      type="button"
      ref={ref}
      // The mark the acceptance run measures on a phone: the control that reveals navigation.
      data-primary-action
      onClick={onClick}
      aria-controls={NAV_ID}
      aria-expanded={isOpen}
      aria-label={isOpen ? 'Đóng menu' : 'Mở menu'}
      className="flex size-9 shrink-0 items-center justify-center rounded-lg transition-colors hover:bg-[hsl(var(--h-muted))] lg:hidden"
    >
      <PanelLeftOpen className="h-5 w-5 text-[hsl(var(--h-card-foreground))]" aria-hidden="true" />
    </button>
  );
}
